import '.d{display:flex;}.ssr-plugin'
import '.e{flex-direction:column;}.ssr-plugin'
import '.ad{background-color:var(--palette-white);}.ssr-plugin'
import '.i2{border-radius:var(--border-radius-image);}.ssr-plugin'
import '.i3{padding:3.5rem 2rem;}.ssr-plugin'
import '.v{align-items:center;}.ssr-plugin'
import '.w{text-align:center;}.ssr-plugin'
import '.x{width:100%;}.ssr-plugin'
import '.bi{height:100%;}.ssr-plugin'
import '.h8{box-sizing:border-box;}.ssr-plugin'
import '.i4{width:12.5rem;}.ssr-plugin'
import '.a{display:grid;}.ssr-plugin'
import '.i5{grid-template-rows:auto 1fr auto;}.ssr-plugin'
import '.bk{flex:1;}.ssr-plugin'
import '.k{justify-content:center;}.ssr-plugin'
import '.i6{padding:0.75rem 0;}.ssr-plugin'
import '.dh{justify-content:flex-end;}.ssr-plugin'
import '.a2{flex-direction:row;}.ssr-plugin'
import '.g{gap:1rem;}.ssr-plugin'
import '.bl{flex-shrink:0;}.ssr-plugin'
import '.c8{position:relative;}.ssr-plugin'
import '.i7{transition:transform 0.25s ease, box-shadow 0.25s ease;}.ssr-plugin'
import '.i8{border:var(--card-border);}.ssr-plugin'
import '.i9{background-color:lightgray;}.ssr-plugin'
import '.ia{background-size:100px 100px, auto;}.ssr-plugin'
import '.ib{background-repeat:repeat, no-repeat;}.ssr-plugin'
import '.ic{background-position:0% 0%, 0% 0%;}.ssr-plugin'
import '.id{background-blend-mode:overlay, normal;}.ssr-plugin'
import { Button } from '@app/components/core/button/Button'
import { Image } from '@app/components/core/image/Image'
import { Paragraph } from '@app/components/core/typography/Paragraph'
import { Title } from '@app/components/core/typography/Title'
import useMediaQuery from '@app/util/useMediaQuery'
import contactCardTexture from '@app/assets/images/background-contact-card.png'
import { type Style, styled } from 'inlines'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import Column from '@app/components/layout/Column'

export type ContactPerson = {
  imageSrc: string
  imageAlt: string
  name: string
  role: string
  buttonLabel: string
  buttonLink: string
}

export type ContactCardProps = {
  title: string
  description?: string
  htmlContent?: string
  contactPeople: ContactPerson[]
  style?: Style
}

const CONTACT_PERSON_CARD_WIDTH = '16.5rem'
const STACK_PEEK_AT_THREE = '10rem'
const STACK_PEEK_AT_FIVE = '3rem'
const STACK_COUNT_FOR_WIDEST_PEEK = 3
const STACK_COUNT_FOR_TIGHTEST_PEEK = 5

const getHorizontalStackPeek = (count: number): string => {
  if (count <= STACK_COUNT_FOR_WIDEST_PEEK) {
    return STACK_PEEK_AT_THREE
  }

  if (count >= STACK_COUNT_FOR_TIGHTEST_PEEK) {
    return STACK_PEEK_AT_FIVE
  }

  const minPeek = parseFloat(STACK_PEEK_AT_THREE)
  const maxPeek = parseFloat(STACK_PEEK_AT_FIVE)
  const progress =
    (count - STACK_COUNT_FOR_WIDEST_PEEK) /
    (STACK_COUNT_FOR_TIGHTEST_PEEK - STACK_COUNT_FOR_WIDEST_PEEK)
  const peek = minPeek + progress * (maxPeek - minPeek)

  return `${peek}rem`
}

const getHorizontalStackOverlap = (peek: string) =>
  `(${CONTACT_PERSON_CARD_WIDTH} - ${peek})`

const ContactPersonCard = ({
  person,
  style,
}: {
  person: ContactPerson
  style?: Style
}) => (
  <styled.div className="d e ad i2 i3 v w x bi h8"
    style={{
      ...style,
    }}
  >
    <Image
      src={person.imageSrc}
      alt={person.imageAlt}
      resizeWidth={144}
      style={{
        width: '9rem',
        height: '9rem',
        borderRadius: '50%',
        objectFit: 'cover',
        objectPosition: 'center top',
        marginBottom: '0.75rem',
      }}
    />
    <div className="i4 a i5 bk"
    >
      <Title.H4
        style={{
          whiteSpace: 'pre-line',
          marginBottom: 0,
        }}
      >
        {person.name}
      </Title.H4>
      <div className="d v k w i6"
      >
        <Paragraph.Medium
          style={{
            marginBottom: 0,
          }}
        >
          {person.role}
        </Paragraph.Medium>
      </div>
      {person.buttonLabel.trim() ? (
        <Button
          size="medium"
          variant="primary"
          label={person.buttonLabel}
          to={person.buttonLink || undefined}
        />
      ) : null}
    </div>
  </styled.div>
)

const ContactPeopleLayout = ({
  contactPeople,
  isMobile,
}: {
  contactPeople: ContactPerson[]
  isMobile: boolean
}) => {
  const count = contactPeople.length

  if (count === 0) {
    return null
  }

  if (isMobile) {
    return (
      <Column>
        {contactPeople.map((person, index) => (
          <ContactPersonCard
            key={`${index}-${person.name}`}
            person={person}
          />
        ))}
      </Column>
    )
  }

  if (count === 1) {
    return (
      <div className="d dh"
        style={{
          width: CONTACT_PERSON_CARD_WIDTH,
        }}
      >
        <ContactPersonCard person={contactPeople[0]} />
      </div>
    )
  }

  if (count === 2) {
    return (
      <div className="d a2 g dh"
      >
        {contactPeople.map((person, index) => (
          <div className="bl"
            key={`${index}-${person.name}`}
            style={{
              width: CONTACT_PERSON_CARD_WIDTH,}}
          >
            <ContactPersonCard person={person} />
          </div>
        ))}
      </div>
    )
  }

  const stackPeek = getHorizontalStackPeek(count)
  const stackOverlap = getHorizontalStackOverlap(stackPeek)

  return (
    <div className="d a2 dh"
      style={{
        width: `calc(${CONTACT_PERSON_CARD_WIDTH} + ${count - 1} * ${stackPeek})`,
      }}
    >
      {contactPeople.map((person, index) => (
        <styled.div className="c8 bl i7 i2 i8"
          key={`${index}-${person.name}`}
          style={{
            width: CONTACT_PERSON_CARD_WIDTH,
            marginLeft: index === 0 ? 0 : `calc(-1 * ${stackOverlap})`,
            zIndex: index + 1,
            '&:hover': {
              zIndex: count + 1,
              transform: 'translateY(-0.5rem)',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
            },
          }}
        >
          <ContactPersonCard person={person} />
        </styled.div>
      ))}
    </div>
  )
}

export const ContactCard = ({
  title,
  description,
  htmlContent,
  style,
  contactPeople,
}: ContactCardProps) => {
  const isMobile = useMediaQuery()

  return (
    <styled.div className="bi i9 ia ib ic id"
      style={{
        backgroundImage: `url(${contactCardTexture}), linear-gradient(177deg, #FFF 13.41%, #FBEBD1 66.89%, #FFCB93 93.89%, #F1A782 115.68%)`,
        borderRadius: isMobile ? '0.5rem' : '3rem',
        padding: isMobile ? '0.5rem' : '4.5rem',
        ...style,
      }}
    >
      <div className="a v"
        style={{
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) auto',
          gap: isMobile ? '2rem' : '9rem',}}
      >
        <styled.div>
          {title && <Title.H2>{title}</Title.H2>}
          {description && <Paragraph.Medium>{description}</Paragraph.Medium>}
          {htmlContent && <SafeHtml html={htmlContent} />}
        </styled.div>

        <ContactPeopleLayout
          contactPeople={contactPeople}
          isMobile={isMobile}
        />
      </div>
    </styled.div>
  )
}
